import { useMemo } from 'react'
import type { CustomerSupportRequest } from '../customerSupportApi'
import { useShopI18n } from '../../i18n'
import {
  buildCustomerAreaNavItems,
  CustomerAreaLayoutShell,
  CustomerAreaMetricCard,
  CustomerAreaSurfaceCard,
} from './customer-area'
import { formatDateTime } from '../helpers/formatters'
import { toSupportStatusBadgeClasses, toSupportStatusLabel } from '../helpers/status'

type CustomerSupportDetailPageProps = {
  supportRequests: CustomerSupportRequest[]
  supportLoading: boolean
  supportError: string
  supportDetailId: string
  onNavigate: (targetPath: string) => void
}

export const CustomerSupportDetailPage = ({
  supportRequests,
  supportLoading,
  supportError,
  supportDetailId,
  onNavigate,
}: CustomerSupportDetailPageProps) => {
  const { language, locale, t, toLocalizedPath } = useShopI18n()
  const selectedRequest = useMemo(
    () => supportRequests.find((request) => request.id === supportDetailId) ?? null,
    [supportDetailId, supportRequests],
  )
  const navItems = useMemo(
    () =>
      buildCustomerAreaNavItems({
        t,
        toLocalizedPath,
        language,
      }),
    [language, t, toLocalizedPath],
  )

  return (
    <CustomerAreaLayoutShell
      eyebrow={t('common.customerArea')}
      title={t('customer.support.detailTitle')}
      subtitle={t('customer.support.detailSubtitle', { ticketId: supportDetailId })}
      navItems={navItems}
      activeNavId="support"
      onNavigate={onNavigate}
      actions={[
        {
          id: 'back-support',
          label: t('customer.support.backToList'),
          onClick: () => onNavigate(toLocalizedPath('/dashboard/support')),
          variant: 'secondary',
        },
      ]}
    >
      {supportError ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {supportError}
        </div>
      ) : null}

      {supportLoading ? (
        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm">
          {t('customer.support.loading')}
        </div>
      ) : null}

      {!supportLoading && !selectedRequest ? (
        <div className="rounded-3xl border border-slate-200 bg-white px-6 py-8 text-center shadow-sm">
          <p className="text-lg font-semibold text-slate-900">{t('customer.support.detailNotFoundTitle')}</p>
          <p className="mt-2 text-sm text-slate-600">{t('customer.support.detailNotFoundDescription')}</p>
        </div>
      ) : null}

      {!supportLoading && selectedRequest ? (
        <>
          <section className="grid gap-3 sm:grid-cols-3">
            <CustomerAreaMetricCard
              label={t('customer.support.detail.ticketId')}
              value={selectedRequest.id}
            />
            <CustomerAreaMetricCard
              label={t('customer.support.detail.status')}
              value={toSupportStatusLabel(selectedRequest.status, language)}
              tone={selectedRequest.status === 'open' ? 'warning' : 'info'}
            />
            <CustomerAreaMetricCard
              label={t('customer.support.detail.updatedAt')}
              value={formatDateTime(selectedRequest.updatedAt, locale, t('common.never'))}
            />
          </section>

          <CustomerAreaSurfaceCard
            title={selectedRequest.subject}
            subtitle={t('customer.support.detail.openedAt', {
              date: formatDateTime(selectedRequest.createdAt, locale, t('common.never')),
            })}
            actions={
              <span
                className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${toSupportStatusBadgeClasses(
                  selectedRequest.status,
                )}`}
              >
                {toSupportStatusLabel(selectedRequest.status, language)}
              </span>
            }
          >
            <div className="space-y-3">
              <article className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {t('customer.support.detail.customerMessage')}
                </p>
                <p className="mt-2 whitespace-pre-wrap text-sm text-slate-800">{selectedRequest.message}</p>
              </article>
              {selectedRequest.adminNotes ? (
                <article className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
                    {t('customer.support.detail.supportReply')}
                  </p>
                  <p className="mt-2 whitespace-pre-wrap text-sm text-emerald-900">{selectedRequest.adminNotes}</p>
                </article>
              ) : (
                <article className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600">
                  {t('customer.support.detail.noReplyYet')}
                </article>
              )}
            </div>
          </CustomerAreaSurfaceCard>
        </>
      ) : null}
    </CustomerAreaLayoutShell>
  )
}
